import React, { useState } from 'react';
import { useAppContext } from '../App';
import FactoriesCarousel from './FactoriesCarousel';
import ProductGrid from './ProductGrid';
import MarketSnapshot from './MarketSnapshot';
import ApplicationsSection from './ApplicationsSection';
import ProductDevSection from './ProductDevSection';

const MainContent = () => {
  const { getFilteredProducts, featuredFactories, applications } = useAppContext();
  const [activeTab, setActiveTab] = useState('all');

  const filteredProducts = getFilteredProducts();

  // Additional filtering by tab
  const tabFilteredProducts = activeTab === 'all'
    ? filteredProducts
    : filteredProducts.filter(p => p.category.toLowerCase() === activeTab);

  return (
    <main className="main-content">
      <div className="container">
        <div className="content-grid">
          {/* Left Column - Primary Content */}
          <div className="content-left">
            {/* Featured Factories */}
            <section className="section animate-fade-in-up delay-2" id="directory">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="title-icon">🏭</span>
                  Featured Factories
                </h2>
                <button className="view-all-btn">
                  View All
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <FactoriesCarousel factories={featuredFactories} />
            </section>

            {/* Hot Products */}
            <section className="section animate-fade-in-up delay-3" id="products-section">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="title-icon">🔥</span>
                  Hot Products
                </h2>
                <div className="tabs">
                  {['all', 'organic', 'inorganic', 'solvent', 'surfactant'].map((tab) => (
                    <button
                      key={tab}
                      className={`tab ${activeTab === tab ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <ProductGrid products={tabFilteredProducts} />
              {tabFilteredProducts.length === 0 && (
                <div className="no-results">
                  <span className="no-results-icon">🔍</span>
                  <p>No products found matching your criteria</p>
                  <button className="btn btn-outline" onClick={() => setActiveTab('all')}>
                    Clear Filters
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* Right Column - Secondary Content */}
          <div className="content-right">
            {/* Market Snapshot */}
            <section className="section animate-slide-in-right delay-4" id="market">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="title-icon">📊</span>
                  Market Snapshot
                </h2>
              </div>
              <MarketSnapshot />
            </section>

            {/* Explore Applications */}
            <section className="section animate-slide-in-right delay-5">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="title-icon">🔬</span>
                  Explore Applications
                </h2>
              </div>
              <ApplicationsSection applications={applications} />
            </section>

            {/* Product Dev Quick-Start */}
            <section className="section animate-slide-in-right delay-6" id="product-dev">
              <ProductDevSection />
            </section>
          </div>
        </div>
      </div>

      <style jsx>{`
        .main-content {
          padding: var(--space-2xl) 0;
        }

        .content-grid {
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: var(--space-xl);
        }

        .content-left {
          display: flex;
          flex-direction: column;
          gap: var(--space-xl);
        }

        .content-right {
          display: flex;
          flex-direction: column;
          gap: var(--space-xl);
        }

        .section {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-xl);
          padding: var(--space-xl);
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-lg);
          gap: var(--space-md);
          flex-wrap: wrap;
        }

        .section-title {
          font-size: var(--font-xl);
          font-weight: 600;
          color: var(--color-neutral-50);
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .title-icon {
          font-size: var(--font-2xl);
        }

        .view-all-btn {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          font-family: var(--font-body);
          font-size: var(--font-sm);
          font-weight: 500;
          color: var(--color-accent-400);
          background: transparent;
          border: none;
          cursor: pointer;
          padding: var(--space-xs) var(--space-sm);
          border-radius: var(--radius-md);
          transition: all var(--transition-fast);
        }

        .view-all-btn:hover {
          background: rgba(59, 130, 246, 0.1);
          color: var(--color-accent-300);
        }

        .tabs {
          display: flex;
          gap: var(--space-xs);
          flex-wrap: wrap;
        }

        .tab {
          font-family: var(--font-body);
          font-size: var(--font-xs);
          font-weight: 500;
          padding: var(--space-xs) var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-md);
          color: var(--color-neutral-400);
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .tab:hover {
          background: rgba(255, 255, 255, 0.08);
          color: var(--color-neutral-200);
        }

        .tab.active {
          background: rgba(59, 130, 246, 0.2);
          border-color: var(--color-accent-400);
          color: var(--color-accent-400);
        }

        .no-results {
          text-align: center;
          padding: var(--space-2xl);
          color: var(--color-neutral-400);
        }

        .no-results-icon {
          font-size: var(--font-4xl);
          display: block;
          margin-bottom: var(--space-md);
        }

        .no-results p {
          margin-bottom: var(--space-lg);
        }

        @media (max-width: 1200px) {
          .content-grid {
            grid-template-columns: 1fr;
          }

          .content-right {
            order: -1;
          }
        }

        @media (max-width: 768px) {
          .section-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .tabs {
            width: 100%;
            overflow-x: auto;
            padding-bottom: var(--space-xs);
          }
        }
      `}</style>
    </main>
  );
};

export default MainContent;
